import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { 
  SubmissionStage, 
  STAGE_CONFIG, 
  SUBMISSION_STAGE_ORDER, 
  getNextStage,
  getStageProgress 
} from '@tvia-ai/shared';

interface StageVisualizationProps {
  currentStage?: SubmissionStage;
  completedStages?: SubmissionStage[];
  className?: string;
  showProgress?: boolean;
  showNextStage?: boolean;
}

const StageIcon = ({ stage, isCompleted, isCurrent }: { 
  stage: SubmissionStage; 
  isCompleted: boolean; 
  isCurrent: boolean;
}) => {
  const iconProps = {
    className: `h-6 w-6 ${
      isCompleted ? 'text-green-600' : 
      isCurrent ? 'text-blue-600' : 
      'text-gray-400'
    }`
  };

  switch (stage) {
    case SubmissionStage.Complaint:
      return <DocumentTextIcon {...iconProps} />;
    case SubmissionStage.DemandLetter:
      return <ExclamationTriangleIcon {...iconProps} />;
    case SubmissionStage.Lawsuit:
      return <ScaleIcon {...iconProps} />;
    default:
      return <DocumentTextIcon {...iconProps} />;
  }
};

const StageVisualization: React.FC<StageVisualizationProps> = ({
  currentStage,
  completedStages = [],
  className = '',
  showProgress = true,
  showNextStage = true,
}) => {
  const { t } = useTranslation();

  const nextStage = currentStage ? getNextStage(currentStage) : null;
  const progress = currentStage ? getStageProgress(currentStage) : 0;

  return (
    <div className={`space-y-6 ${className}`}>
      {showProgress && currentStage && (
        <div className="w-full">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>{t('lawsuit.stageProgress', 'Stage Progress')}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      <div className="relative">
        <div className="absolute left-4 top-8 bottom-8 w-0.5 bg-gray-200" />
        
        {SUBMISSION_STAGE_ORDER.map((stage) => {
          const isCompleted = completedStages.includes(stage);
          const isCurrent = currentStage === stage;
          const config = STAGE_CONFIG[stage];
          
          return (
            <div key={stage} className="relative flex items-start mb-8 last:mb-0">
              <div className={`
                relative z-10 flex items-center justify-center w-8 h-8 rounded-full
                ${isCompleted ? 'bg-green-100 border-2 border-green-600' :
                  isCurrent ? 'bg-blue-100 border-2 border-blue-600' :
                  'bg-gray-100 border-2 border-gray-300'}
              `}>
                {isCompleted ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                ) : (
                  <div className={`w-3 h-3 rounded-full ${
                    isCurrent ? 'bg-blue-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>

              <div className="ml-4 flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <StageIcon stage={stage} isCompleted={isCompleted} isCurrent={isCurrent} />
                  <h3 className={`text-sm font-medium ${
                    isCompleted ? 'text-green-900' :
                    isCurrent ? 'text-blue-900' :
                    'text-gray-600'
                  }`}>
                    {t(config.title, `Stage ${config.order}`)}
                  </h3>
                  {isCurrent && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {t('lawsuit.current', 'Current')}
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-2">
                  {t(config.description, 'Stage description')}
                </p>
                
                <div className="flex items-center text-xs text-gray-500">
                  <ClockIcon className="h-3 w-3 mr-1" />
                  <span>
                    {t('lawsuit.estimatedDuration', 'Est. {{days}} days', { 
                      days: config.estimatedDays 
                    })}
                  </span>
                  {config.responseDeadlineDays && (
                    <>
                      <span className="mx-2">•</span>
                      <span>
                        {t('lawsuit.responseDeadline', '{{days}} days to respond', { 
                          days: config.responseDeadlineDays 
                        })}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showNextStage && nextStage && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
          <div className="flex items-center space-x-2 mb-2">
            <ArrowRightIcon className="h-4 w-4 text-blue-600" />
            <h4 className="text-sm font-medium text-gray-900">
              {t('lawsuit.nextStage', 'Next Stage')}
            </h4>
          </div>
          
          <div className="flex items-center space-x-2">
            <StageIcon stage={nextStage} isCompleted={false} isCurrent={false} />
            <div>
              <p className="text-sm font-medium text-gray-900">
                {t(STAGE_CONFIG[nextStage].title, 'Next stage')}
              </p>
              <p className="text-xs text-gray-600">
                {t(STAGE_CONFIG[nextStage].description, 'Next stage description')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StageVisualization;
